import { type ReactNode, useId, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { usePageContext } from '~/context/usePageContext';
import useToast, { type ToastData } from '~/hooks/useToast';
import DataLayer from '~/services/DataLayer';

import ActionForm from '../ActionForm';
import { useComponentFetcher } from '../ComponentErrorBoundary/useComponentFetcher';
import StatusInputs from '../Forms/StatusInputs';
import ClientSideInputs from './_components/ClientSideInputs';
import ServerSideInputs from './_components/ServerSideInputs';

type RenderProps = {
  isLoading: boolean;
};

type ContactFormActionProps = {
  id: string;
  pageId?: string;
  name?: string;
  redirectUrl?: string;
  /** Map of field name (question id) to prompt label for email display */
  fieldLabels?: Record<string, string>;
  webhookUrl?: string;
  children: (props: RenderProps) => ReactNode;
  onSuccess?: (data: { toast?: ToastData }) => void;
};

const ContactFormAction = ({
  name,
  id,
  pageId,
  redirectUrl,
  children,
  onSuccess,
  fieldLabels,
  webhookUrl,
}: ContactFormActionProps) => {
  const fallbackId = useId();
  const fetcher = useComponentFetcher<() => Promise<{ toast?: ToastData }>>();
  const isLoading = fetcher.state === 'submitting';
  const { t } = useTranslation();
  const { visitToken, currentPath } = usePageContext();
  const formRef = useRef<HTMLFormElement>(null);

  const formId = id || fallbackId;

  const handleSuccess = (data: any) => {
    window._pxFormId = undefined;

    formRef.current?.reset();

    if (data?.dataLayerEvent) {
      DataLayer.pushEvent(data.dataLayerEvent);
    }

    if (data?.redirectTo) {
      window.location.href = data.redirectTo;
      return;
    }

    onSuccess?.(data);
  };

  const handleError = (_data: any) => {
    window._pxFormId = undefined;
  };

  useToast(fetcher?.data?.toast);

  return (
    <ActionForm
      id={formId}
      fetcher={fetcher}
      formRef={formRef}
      method="post"
      action="/contact_form_response"
      onSuccess={handleSuccess}
      onError={handleError}
    >
      <StatusInputs
        name="contact_form_response"
        successMessage={t('forms.toast.success')}
        errorMessage={t('errors.default')}
      />
      <ClientSideInputs
        name={name}
        visitToken={visitToken}
        redirectUrl={redirectUrl}
        fallbackPath={currentPath}
        webhookUrl={webhookUrl}
      />
      <ServerSideInputs />
      {pageId && <input hidden name="page_id" defaultValue={pageId} />}
      {id && <input hidden name="contact_form_node_id" defaultValue={id} />}
      {fieldLabels && Object.keys(fieldLabels).length > 0 && (
        <input hidden name="field_labels" defaultValue={JSON.stringify(fieldLabels)} />
      )}
      {children({ isLoading })}
    </ActionForm>
  );
};

export default ContactFormAction;
